/**
 * Claim Service
 *
 * Builds and sends on-chain claim transactions via the Takara smart contract.
 * Accepts a signed payload from the backend, constructs the transaction
 * (ed25519 precompile + claim instruction), and sends it via the Phantom wallet.
 *
 * Flow:
 *   1. Backend signs the claim → returns { signature, amountRaw, nonce, ... }
 *   2. This service builds the Solana transaction
 *   3. Phantom signs → sendRawTransaction → confirmed
 */

import {
  Connection,
  PublicKey,
  VersionedTransaction,
  TransactionMessage,
  Ed25519Program,
  SYSVAR_INSTRUCTIONS_PUBKEY,
  SystemProgram,
  ComputeBudgetProgram,
} from '@solana/web3.js'
import {
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountIdempotentInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import * as anchor from '@coral-xyz/anchor'
import BN from 'bn.js'
import { CONTRACT_CONFIG } from '../config/contract'
import idl from '../idl/claim_takara.json'
import i18n from '../i18n/config'


// ── Types ─────────────────────────────────────────────────────────────────────

export type ClaimType = 'reward_usdt' | 'reward_takara' | 'referral_usdt' | 'referral_takara'

export interface ClaimPayload {
  claimLogId:       string
  claimType:        ClaimType
  signature:        number[]   // 64-byte ed25519 signature as number[]
  amountRaw:        string     // raw u64 as decimal string (e.g. "50000000" = 50 USDT)
  taxAmountRaw?:    string     // only for TAKARA claims
  nonce:            string     // u64 as decimal string
  expiryTimestamp:  string     // Unix timestamp (seconds) as decimal string
  usdtMint?:        string
  takaraMint?:      string
  platformWallet?:  string
  poolPda:          string
  programId:        string
  expiresAt:        string
}

// sendTransaction from useWallet() — Phantom trusts this path (signAndSend)
// and won't show "dApp may be malicious" warnings.
type SendTransactionFn = (
  tx: VersionedTransaction,
  connection: Connection,
  options?: { skipPreflight?: boolean; maxRetries?: number },
) => Promise<string>

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Build and send an on-chain claim transaction.
 * Returns the Solana transaction signature (base58).
 */
export async function sendClaimTransaction(
  payload: ClaimPayload,
  userPublicKey: PublicKey,
  sendTransaction: SendTransactionFn,
): Promise<string> {
  const connection = new Connection(CONTRACT_CONFIG.directRpcUrl, {
    commitment:  'confirmed',
    wsEndpoint:  CONTRACT_CONFIG.directWsUrl,
  })

  // Minimal wallet shim for AnchorProvider — actual signing is done via signTransaction param
  const walletShim = {
    publicKey: userPublicKey,
    signTransaction: async (tx: VersionedTransaction) => tx,
    signAllTransactions: async (txs: VersionedTransaction[]) => txs,
  }

  const provider = new anchor.AnchorProvider(connection, walletShim as any, {
    commitment: 'confirmed',
  })
  // Override IDL address with runtime programId from payload so PDAs and instructions
  // target the same program (IDL may contain a different deployment address).
  const programId = new PublicKey(payload.programId)
  const idlWithAddress = { ...idl, address: payload.programId }
  const program = new anchor.Program(idlWithAddress as anchor.Idl, provider)
  const poolPda   = new PublicKey(payload.poolPda)

  // PDAs
  const [globalStatePda] = PublicKey.findProgramAddressSync(
    [Buffer.from('global')],
    programId,
  )
  const [userStatePda] = PublicKey.findProgramAddressSync(
    [Buffer.from('user'), userPublicKey.toBuffer()],
    programId,
  )

  // Build message bytes — must match backend claim-signing.service.ts exactly
  const messageBytes = buildMessage(payload, userPublicKey)

  // Instruction 0: ed25519 precompile verifies operator signature
  const ed25519Ix = Ed25519Program.createInstructionWithPublicKey({
    publicKey: CONTRACT_CONFIG.operatorPubkey.toBytes(),
    message:   messageBytes,
    signature: Uint8Array.from(payload.signature),
  })

  // ComputeBudget first, then ed25519; contract searches for ed25519 dynamically by program_id
  const txInstructions: anchor.web3.TransactionInstruction[] = [
    ComputeBudgetProgram.setComputeUnitLimit({ units: 400_000 }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 1_000 }),
    ed25519Ix,
  ]

  // Optional init_user_state if UserState PDA doesn't exist yet (goes after ed25519)
  const userStateInfo = await connection.getAccountInfo(userStatePda)
  if (!userStateInfo) {
    const initIx = await program.methods
      .initUserState()
      .accountsStrict({
        payer:         userPublicKey,
        user:          userPublicKey,
        userState:     userStatePda,
        systemProgram: SystemProgram.programId,
      })
      .instruction()
    txInstructions.push(initIx)
  }

  // Ensure recipient ATAs exist (idempotent — no-op if already created)
  const ataIxs = buildEnsureAtaInstructions(payload, userPublicKey, poolPda)
  txInstructions.push(...ataIxs)

  // Claim instruction
  const claimIx = await buildClaimInstruction(
    program,
    payload,
    userPublicKey,
    globalStatePda,
    userStatePda,
    poolPda,
  )
  txInstructions.push(claimIx)

  // Build versioned transaction (V0) — required for StandardWalletAdapter compatibility
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
  const message = new TransactionMessage({
    payerKey:         userPublicKey,
    recentBlockhash:  blockhash,
    instructions:     txInstructions,
  }).compileToV0Message()
  const tx = new VersionedTransaction(message)

  // Pre-flight simulation BEFORE wallet signing.
  // Phantom does its own simulation on signTransaction and throws a generic error
  // without logs. By simulating first, we get detailed program logs and can show
  // user-friendly messages (e.g. "ClaimTooFrequent" → "once every 24 hours").
  // sigVerify: false — transaction is unsigned at this point; skip signature check
  // so simulation can run. Per Phantom docs, this helps avoid "malicious dApp" warnings.
  const simResult = await connection.simulateTransaction(tx, {
    commitment: 'confirmed',
    sigVerify: false,
  })
  if (simResult.value.err) {
    const simLogs = simResult.value.logs ?? []
    const simError = new Error(
      `Transaction simulation failed: ${JSON.stringify(simResult.value.err)}`
    ) as any
    simError.logs = simLogs
    throw enrichSolanaError(simError)
  }

  // Simulation passed — use wallet adapter's sendTransaction (signAndSend).
  // Phantom trusts this path and won't show "dApp may be malicious" warnings,
  // because the wallet controls both signing and sending.
  let txSignature: string
  try {
    txSignature = await sendTransaction(tx, connection, { skipPreflight: false })
  } catch (sendErr: any) {
    throw enrichSolanaError(sendErr)
  }

  // Confirm via HTTP polling — blockhash strategy avoids WebSocket (devnet WS is unreliable).
  // If confirmation times out or fails, attach txSignature to the error so the caller can
  // still notify the backend (the tx may have landed even if confirmation polling failed).
  try {
    await connection.confirmTransaction(
      { signature: txSignature, blockhash, lastValidBlockHeight },
      'confirmed',
    )
  } catch (confirmErr: any) {
    confirmErr.txSignature = txSignature
    throw confirmErr
  }

  return txSignature
}

/**
 * Enrich a Solana error with logs and hex code for parseClaimError to find.
 * Extracts logs from various locations where @solana/web3.js might put them.
 */
function enrichSolanaError(err: any): Error {
  const logs: string[] = err?.logs
    ?? err?.transactionLogs
    ?? err?.data?.logs
    ?? []
  const logsStr = Array.isArray(logs) ? logs.join('\n') : ''
  const originalMsg = err?.message ?? ''

  // Build a message that parseClaimError can always match
  const enrichedMsg = logsStr
    ? `${originalMsg}\n[logs] ${logsStr}`
    : originalMsg

  const enriched = new Error(enrichedMsg) as any
  enriched.logs = logs
  enriched._enriched = true
  // Preserve wallet-related properties
  if (err?.txSignature) enriched.txSignature = err.txSignature
  if (err?.response) enriched.response = err.response
  return enriched
}

// ── Message builder ───────────────────────────────────────────────────────────
// Must match backend/src/services/claim-signing.service.ts exactly.
// Format: <label_bytes> + user_pubkey(32) + mint(32) + amount_le(8) + nonce_le(8) [+ tax_le(8)] + expiry_le(8)

function buildMessage(payload: ClaimPayload, userPublicKey: PublicKey): Uint8Array {
  const { claimType, amountRaw, taxAmountRaw, nonce, expiryTimestamp } = payload

  const isUsdt = claimType === 'reward_usdt' || claimType === 'referral_usdt'
  const mint   = isUsdt ? CONTRACT_CONFIG.usdtMint : CONTRACT_CONFIG.takaraMint

  const parts: Buffer[] = [
    Buffer.from(claimType, 'ascii'),
    Buffer.from(userPublicKey.toBytes()),
    Buffer.from(mint.toBytes()),
    toLE64(BigInt(amountRaw)),
    toLE64(BigInt(nonce)),
  ]

  if (taxAmountRaw !== undefined) {
    parts.push(toLE64(BigInt(taxAmountRaw)))
  }

  parts.push(toLE64(BigInt(expiryTimestamp)))

  return Buffer.concat(parts)
}

function toLE64(n: bigint): Buffer {
  const b = Buffer.alloc(8)
  b.writeBigUInt64LE(n)
  return b
}

// ── ATA creation helper ───────────────────────────────────────────────────────
// Creates idempotent ATA init instructions so the claim tx doesn't fail if the
// recipient token accounts don't exist yet.

function buildEnsureAtaInstructions(
  payload: ClaimPayload,
  userPublicKey: PublicKey,
  _poolPda: PublicKey,
): anchor.web3.TransactionInstruction[] {
  const { claimType } = payload
  const usdtMint   = CONTRACT_CONFIG.usdtMint
  const takaraMint = CONTRACT_CONFIG.takaraMint

  const makeAta = (
    mint: PublicKey,
    owner: PublicKey,
    allowOwnerOffCurve = false,
  ) => {
    const ata = getAssociatedTokenAddressSync(mint, owner, allowOwnerOffCurve)
    return createAssociatedTokenAccountIdempotentInstruction(
      userPublicKey,   // payer
      ata,
      owner,
      mint,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID,
    )
  }

  if (claimType === 'reward_usdt' || claimType === 'referral_usdt') {
    return [makeAta(usdtMint, userPublicKey)]
  }

  if (claimType === 'reward_takara' || claimType === 'referral_takara') {
    if (!payload.platformWallet) throw new Error('platformWallet missing in payload')
    const platformWallet = new PublicKey(payload.platformWallet)
    return [
      makeAta(takaraMint, userPublicKey),
      makeAta(takaraMint, platformWallet),
    ]
  }

  return []
}

// ── Claim instruction builder ─────────────────────────────────────────────────

async function buildClaimInstruction(
  program: anchor.Program,
  payload: ClaimPayload,
  userPublicKey: PublicKey,
  globalStatePda: PublicKey,
  userStatePda: PublicKey,
  poolPda: PublicKey,
): Promise<anchor.web3.TransactionInstruction> {
  const { claimType, amountRaw, taxAmountRaw, nonce, expiryTimestamp, signature } = payload
  const sig               = Array.from(signature) as number[]
  const amountBN          = new BN(amountRaw)
  const nonceBN           = new BN(nonce)
  const expiryTimestampBN = new BN(expiryTimestamp)

  const usdtMint   = CONTRACT_CONFIG.usdtMint
  const takaraMint = CONTRACT_CONFIG.takaraMint

  if (claimType === 'reward_usdt') {
    const poolUsdtAta = getAssociatedTokenAddressSync(usdtMint, poolPda, true)
    const userUsdtAta = getAssociatedTokenAddressSync(usdtMint, userPublicKey)
    return program.methods
      .claimRewardUsdt(amountBN, nonceBN, expiryTimestampBN, sig)
      .accountsStrict({
        user:               userPublicKey,
        globalState:        globalStatePda,
        pool:               poolPda,
        userState:          userStatePda,
        usdtMint,
        poolUsdtAccount:    poolUsdtAta,
        userUsdtAccount:    userUsdtAta,
        instructionsSysvar: SYSVAR_INSTRUCTIONS_PUBKEY,
        tokenProgram:       TOKEN_PROGRAM_ID,
      })
      .instruction()
  }

  if (claimType === 'reward_takara') {
    const platformWallet    = new PublicKey(payload.platformWallet!)
    const poolTakaraAta     = getAssociatedTokenAddressSync(takaraMint, poolPda, true)
    const userTakaraAta     = getAssociatedTokenAddressSync(takaraMint, userPublicKey)
    const platformTakaraAta = getAssociatedTokenAddressSync(takaraMint, platformWallet)
    return program.methods
      .claimRewardTakara(amountBN, new BN(taxAmountRaw!), nonceBN, expiryTimestampBN, sig)
      .accountsStrict({
        user:                  userPublicKey,
        globalState:           globalStatePda,
        pool:                  poolPda,
        userState:             userStatePda,
        takaraMint,
        poolTakaraAccount:     poolTakaraAta,
        userTakaraAccount:     userTakaraAta,
        platformWallet,
        platformTakaraAccount: platformTakaraAta,
        instructionsSysvar:    SYSVAR_INSTRUCTIONS_PUBKEY,
        tokenProgram:          TOKEN_PROGRAM_ID,
      })
      .instruction()
  }

  if (claimType === 'referral_usdt') {
    const poolUsdtAta     = getAssociatedTokenAddressSync(usdtMint, poolPda, true)
    const referrerUsdtAta = getAssociatedTokenAddressSync(usdtMint, userPublicKey)
    return program.methods
      .claimReferralUsdt(amountBN, nonceBN, expiryTimestampBN, sig)
      .accountsStrict({
        referrer:            userPublicKey,
        globalState:         globalStatePda,
        pool:                poolPda,
        userState:           userStatePda,
        usdtMint,
        poolUsdtAccount:     poolUsdtAta,
        referrerUsdtAccount: referrerUsdtAta,
        instructionsSysvar:  SYSVAR_INSTRUCTIONS_PUBKEY,
        tokenProgram:        TOKEN_PROGRAM_ID,
      })
      .instruction()
  }

  if (claimType === 'referral_takara') {
    const platformWallet    = new PublicKey(payload.platformWallet!)
    const poolTakaraAta     = getAssociatedTokenAddressSync(takaraMint, poolPda, true)
    const referrerTakaraAta = getAssociatedTokenAddressSync(takaraMint, userPublicKey)
    const platformTakaraAta = getAssociatedTokenAddressSync(takaraMint, platformWallet)
    return program.methods
      .claimReferralTakara(amountBN, new BN(taxAmountRaw!), nonceBN, expiryTimestampBN, sig)
      .accountsStrict({
        referrer:               userPublicKey,
        globalState:            globalStatePda,
        pool:                   poolPda,
        userState:              userStatePda,
        takaraMint,
        poolTakaraAccount:      poolTakaraAta,
        referrerTakaraAccount:  referrerTakaraAta,
        platformWallet,
        platformTakaraAccount:  platformTakaraAta,
        instructionsSysvar:     SYSVAR_INSTRUCTIONS_PUBKEY,
        tokenProgram:           TOKEN_PROGRAM_ID,
      })
      .instruction()
  }

  throw new Error(`Unknown claimType: ${claimType}`)
}

// ── Contract error parser ─────────────────────────────────────────────────────

export function parseClaimError(err: any): string {
  const t = (key: string, opts?: Record<string, unknown>) => i18n.t(key, opts)

  const msg: string = err?.message ?? err?.toString() ?? 'Unknown error'
  const backendMsg: string = err?.response?.data?.message ?? ''
  const statusCode: number = err?.response?.status ?? 0
  const errLogs: string[] = err?.logs ?? err?.transactionLogs ?? err?.data?.logs ?? []
  const logs: string = Array.isArray(errLogs) ? errLogs.join(' ') : ''
  const errStr: string = typeof err === 'object' ? JSON.stringify(err).slice(0, 2000) : ''
  const combined = msg + ' ' + backendMsg + ' ' + logs + ' ' + errStr

  // ── Contract error codes (hex "0xNNNN" or JSON {"Custom":NNNN}) ────
  const hexMatch = combined.match(/custom program error: 0x([0-9a-fA-F]+)/)
  const jsonMatch = combined.match(/"Custom"\s*:\s*(\d+)/)
  const errorCode = hexMatch
    ? parseInt(hexMatch[1], 16)
    : jsonMatch
      ? parseInt(jsonMatch[1], 10)
      : null

  const CONTRACT_ERRORS: Record<number, string> = {
    6000: t('errors:claim.contractPaused'),
    6001: t('errors:claim.unauthorized'),
    6002: t('errors:claim.verificationFailed'),
    6003: t('errors:claim.sessionExpired'),
    6004: t('errors:claim.sessionOutOfSync'),
    6005: t('errors:claim.poolLow'),
    6006: t('errors:claim.invalidAmount'),
    6007: t('errors:claim.technicalError'),
    6008: t('errors:claim.txBuildError'),
    6009: t('errors:claim.txBuildError'),
    6010: t('errors:claim.technicalError'),
    6011: t('errors:claim.invalidAddress'),
    6012: t('errors:claim.technicalError'),
    6013: t('errors:claim.claimTooFrequent'),
  }
  if (errorCode !== null && CONTRACT_ERRORS[errorCode]) {
    return CONTRACT_ERRORS[errorCode]
  }

  // ── On-chain 24h cooldown (caught by backend before signing) ─────────
  // Backend returns remainingSeconds in err.response.data.data when cooldown is active.
  // Check this before generic string matching so we can show remaining hours.
  const remainingSecs: number | undefined = err?.response?.data?.data?.remainingSeconds
  if (
    remainingSecs !== undefined ||
    combined.includes('can only be claimed once every 24 hours') ||
    combined.includes('ClaimTooFrequent')
  ) {
    if (remainingSecs !== undefined && remainingSecs > 0) {
      const hours = Math.ceil(remainingSecs / 3600)
      const withHours = t('errors:claim.claimTooFrequentHours', { hours })
      // If the i18n key exists and wasn't returned as the key itself, use it; otherwise fallback
      return withHours !== 'errors:claim.claimTooFrequentHours'
        ? withHours
        : t('errors:claim.claimTooFrequent')
    }
    return t('errors:claim.claimTooFrequent')
  }

  // ── Rate limit (429) ──────────────────────────────────────────────────
  if (statusCode === 429 || combined.includes('Too many claim'))
    return t('errors:claim.rateLimit')

  // ── Wallet / user actions ─────────────────────────────────────────────
  if (combined.includes('User rejected the request') ||
      combined.includes('Transaction cancelled') ||
      combined.includes('Transaction rejected'))
    return t('errors:claim.cancelled')

  if (combined.includes('Popup closed') || combined.includes('Popup window closed'))
    return t('errors:claim.walletPopupClosed')

  if (combined.includes('WalletDisconnectedError') || combined.includes('Wallet disconnected'))
    return t('errors:claim.walletDisconnected')

  if (combined.includes('Wallet not connected'))
    return t('errors:claim.walletNotConnected')

  if (combined.includes('Insufficient SOL') || combined.includes('insufficient lamports'))
    return t('errors:claim.insufficientSol')

  if (combined.includes('Transaction too large') || combined.includes('too large'))
    return t('errors:claim.txTooLarge')

  if (combined.includes('Ledger') || combined.includes('ledger'))
    return t('errors:claim.ledgerError')

  // ── Claim already in progress ─────────────────────────────────────────
  if (combined.includes('already in progress') || combined.includes('ALREADY_IN_PROGRESS'))
    return t('errors:claim.alreadyInProgress')

  // ── Contract errors (by name from logs) ───────────────────────────────
  if (combined.includes('ClaimTooFrequent'))
    return t('errors:claim.claimTooFrequent')

  if (combined.includes('ContractPaused'))
    return t('errors:claim.contractPaused')

  if (combined.includes('SignatureExpired'))
    return t('errors:claim.sessionExpired')

  if (combined.includes('InsufficientFunds'))
    return t('errors:claim.poolLow')

  if (combined.includes('InvalidNonce'))
    return t('errors:claim.sessionOutOfSync')

  if (combined.includes('InvalidSignature') ||
      combined.includes('SignatureVerificationFailed'))
    return t('errors:claim.verificationFailed')

  if (combined.includes('InvalidEd25519Instruction') ||
      combined.includes('MissingEd25519Instruction'))
    return t('errors:claim.txBuildError')

  if (combined.includes('AccountNotInitialized'))
    return t('errors:claim.accountNotInitialized')

  // ── On-chain result errors ────────────────────────────────────────────
  if (combined.includes('NONCE_MISMATCH'))
    return t('errors:claim.nonceMismatch')

  if (combined.includes('TX_FAILED'))
    return t('errors:claim.txFailed')

  // ── NFT / ownership errors ────────────────────────────────────────────
  if (combined.includes('do not own this') || combined.includes('not NFT owner'))
    return t('errors:claim.notNftOwner')

  if (combined.includes('NFT must be minted') || combined.includes('mint your NFT'))
    return t('errors:claim.mintNftFirst')

  // ── Blockhash / confirmation expiry (MUST be before generic network) ──
  if (combined.includes('block height exceeded') ||
      combined.includes('BlockhashNotFound') ||
      combined.includes('Blockhash not found'))
    return t('errors:claim.txExpired')

  if (combined.includes('was not confirmed') ||
      combined.includes('confirmation timeout') ||
      combined.includes('TransactionExpiredTimeoutError'))
    return t('errors:claim.txMaySentTimeout')

  // ── Server / network errors ───────────────────────────────────────────
  if (combined.includes('temporarily unavailable') || statusCode === 503)
    return t('errors:claim.serviceUnavailable')

  if (combined.includes('Unable to verify NFT'))
    return t('errors:claim.nftVerifyFailed')

  if (statusCode === 500 || combined.includes('Internal'))
    return t('errors:claim.serverError')

  if (combined.includes('Network') || combined.includes('ECONNREFUSED') ||
      combined.includes('ETIMEDOUT') || combined.includes('timeout') ||
      combined.includes('Failed to fetch'))
    return t('errors:claim.networkError')

  // ── Technical errors (contact support) ────────────────────────────────
  if (combined.includes('InvalidAmount') ||
      combined.includes('InvalidOperatorKey') ||
      combined.includes('ArithmeticOverflow') ||
      combined.includes('InvalidAddress') ||
      combined.includes('Unauthorized'))
    return t('errors:claim.technicalError')

  // ── Backend message passthrough ───────────────────────────────────────
  if (backendMsg) return backendMsg

  return t('errors:claim.unknown')
}
